import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Edit3, Eye, EyeOff, Plus, Star, StarOff, Trash2, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { EmptyState, Skeleton } from "@/components/admin/Field";
import ConfirmDialog from "@/components/admin/ConfirmDialog";

export default function ProjectsListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/projects");
      setItems(data || []);
    } catch (e) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function quickToggle(item, key) {
    try {
      await api.put(`/admin/projects/${item.id}`, { [key]: !item[key] });
      setItems((arr) => arr.map((p) => (p.id === item.id ? { ...p, [key]: !p[key] } : p)));
    } catch {
      toast.error("Update failed");
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/projects/${pendingDelete.id}`);
      setItems((arr) => arr.filter((p) => p.id !== pendingDelete.id));
      toast.success("Project deleted");
      setPendingDelete(null);
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="px-6 py-8 md:px-10 md:py-12">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#0055ff]">Portfolio</span>
          <h1 className="mt-1 font-display text-3xl font-medium tracking-tight text-white md:text-4xl">Projects</h1>
          <p className="mt-1 font-body text-sm text-white/50">Create, organise and publish portfolio case studies.</p>
        </div>
        <button
          onClick={() => navigate("/admin/portfolio/new")}
          data-testid="projects-new-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#0055ff] px-5 py-2.5 font-body text-sm font-medium text-white transition hover:bg-[#1a66ff]"
        >
          <Plus className="h-4 w-4" />
          New project
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="No projects yet"
          description="Create your first project to populate the public portfolio."
          action={
            <Link
              to="/admin/portfolio/new"
              className="inline-flex items-center gap-2 rounded-full bg-[#0055ff] px-5 py-2.5 font-body text-sm font-medium text-white hover:bg-[#1a66ff]"
            >
              <Plus className="h-4 w-4" /> New project
            </Link>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => (
            <article
              key={p.id}
              data-testid={`project-card-${p.slug}`}
              className="group relative flex flex-col overflow-hidden rounded-3xl border border-white/[0.07] bg-white/[0.015] transition hover:border-white/15"
            >
              <div className="relative h-44 w-full overflow-hidden bg-[#0a0a0e]">
                {p.cover_image ? (
                  <img
                    src={p.cover_image}
                    alt={p.title}
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                  />
                ) : (
                  <div className="mock-grad-1 h-full w-full" />
                )}
                <div className="absolute left-3 top-3 flex gap-2">
                  {p.featured && (
                    <span className="rounded-full bg-[#0055ff] px-2.5 py-1 font-body text-[10px] font-medium uppercase tracking-[0.2em] text-white">
                      Featured
                    </span>
                  )}
                  {!p.published && (
                    <span className="rounded-full bg-white/10 px-2.5 py-1 font-body text-[10px] uppercase tracking-[0.2em] text-white/80">
                      Draft
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="font-body text-[11px] tracking-[0.25em] uppercase text-white/40">{p.category || "—"}</div>
                <h3 className="mt-1.5 font-display text-lg font-medium tracking-tight text-white">{p.title}</h3>
                <p className="mt-1.5 line-clamp-2 font-body text-sm text-white/55">{p.short_description}</p>
                <div className="mt-auto flex items-center gap-2 pt-5">
                  <button
                    onClick={() => navigate(`/admin/portfolio/${p.id}`)}
                    data-testid={`project-edit-${p.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3.5 py-1.5 font-body text-xs text-white/85 transition hover:border-[#0055ff]/40 hover:text-white"
                  >
                    <Edit3 className="h-3 w-3" /> Edit
                  </button>
                  <button
                    onClick={() => quickToggle(p, "published")}
                    title={p.published ? "Unpublish" : "Publish"}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-white/12 text-white/70 hover:border-white/30 hover:text-white"
                  >
                    {p.published ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    onClick={() => quickToggle(p, "featured")}
                    title={p.featured ? "Unfeature" : "Feature"}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-white/12 text-white/70 hover:border-white/30 hover:text-white"
                  >
                    {p.featured ? <Star className="h-3.5 w-3.5" /> : <StarOff className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    onClick={() => setPendingDelete(p)}
                    data-testid={`project-delete-${p.slug}`}
                    title="Delete"
                    className="ml-auto flex h-8 w-8 items-center justify-center rounded-full border border-white/12 text-white/55 transition hover:border-red-400/40 hover:text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this project?"
        description={`“${pendingDelete?.title || ""}” will be removed from the database. This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
